import type { SeasonPodiumEntry, SeasonSummary } from '@ottd/api';
import { useSeasons } from '@ottd/api';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { PageHeader } from '@/components/prototype/page-header';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { QueryState } from '@/components/prototype/query-state';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { useDaysUntil } from '@/hooks/use-countdown';
import { formatNumber, formatSignedPoints } from '@/lib/format';
import { readPage, withPage } from '@/lib/page-param';
import { cn } from '@/lib/utils';
import { formatDateRange, PlayerLink } from '@/pages/shared';

/** The old site's page param, so inbound legacy links keep their page. */
const LEGACY_PAGE = 'page_seasons';

export default function SeasonsPage() {
  const { t } = useTranslation('season');
  const [params, setParams] = useSearchParams();
  const page = readPage(params, LEGACY_PAGE);

  const { data, isLoading, isError, error } = useSeasons(page);
  const seasons = data?.data ?? [];
  const current = seasons.find((season) => season.isCurrent);
  const past = seasons.filter((season) => !season.isCurrent);

  const currentDaysLeft = useDaysUntil(current?.endDate);

  const setPage = (next: number) => {
    setParams(withPage(params, next, LEGACY_PAGE), { replace: true });
  };

  return (
    <div className="mx-auto max-w-[1080px] px-4 py-6 sm:px-5">
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} subtitle={t('subtitle')} />

      <QueryState
        className="mt-8 flex flex-col gap-6"
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={seasons.length === 0}
        emptyLabel={t('empty')}
      >
        {current ? (
          <BevelWindow
            variant="orange"
            shadow="lg"
            title={t('current.title', { num: current.num })}
            action={t('current.endsIn', {
              days: current.endsInDays ?? currentDaysLeft ?? '-',
            })}
          >
            <div className="grid gap-4 md:grid-cols-[1fr_240px]">
              <PodiumList podium={current.podium} highlightLeader />
              <div className="border-line flex flex-col items-center justify-center gap-1 text-center md:border-l md:pl-4">
                <div className="font-display text-strong text-2xl">
                  {formatNumber(current.gamesCount)}
                </div>
                <div className="text-faint text-[10px] uppercase tracking-wide">
                  {t('current.gamesSoFar')}
                </div>
                <div className="text-dim mt-1 font-mono text-xs">
                  {formatDateRange(current.startDate, current.endDate)}
                </div>
                <Button asChild variant="primary" size="sm" className="mt-2">
                  <Link to={`/season/${current.num}`}>{t('current.viewLadder')}</Link>
                </Button>
              </div>
            </div>
          </BevelWindow>
        ) : null}

        {past.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {past.map((season) => (
              <PastSeasonCard key={season.num} season={season} />
            ))}
          </div>
        ) : null}

        {data ? (
          <Pagination
            page={data.meta.current_page}
            lastPage={data.meta.last_page}
            onPageChange={setPage}
          />
        ) : null}
      </QueryState>
    </div>
  );
}

function PastSeasonCard({ season }: { season: SeasonSummary }) {
  const { t } = useTranslation('season');
  const { t: tc } = useTranslation('common');

  return (
    <BevelWindow
      title={t('past.title', { num: season.num })}
      action={tc('units.games', { count: season.gamesCount })}
    >
      <p className="text-faint mb-2 font-mono text-[11px]">
        {formatDateRange(season.startDate, season.endDate)}
      </p>
      <PodiumList podium={season.podium} />
      <div className="mt-2 text-right">
        <Link
          to={`/season/${season.num}`}
          className="font-display text-orange inline-flex items-center gap-1 text-[10px] uppercase"
        >
          {tc('fullResults')}
          <ArrowRight className="size-3" aria-hidden />
        </Link>
      </div>
    </BevelWindow>
  );
}

function PodiumList({
  podium,
  highlightLeader = false,
}: {
  podium: SeasonPodiumEntry[];
  highlightLeader?: boolean;
}) {
  const { t } = useTranslation('season');
  const { t: tc } = useTranslation('common');

  if (podium.length === 0) {
    return <p className="text-faint font-mono text-xs">{t('podium.empty')}</p>;
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {podium.map((entry) => {
        const isLeader = entry.rank === 1;
        return (
          <li
            key={entry.rank}
            className={cn(
              'flex items-center gap-2 px-2 py-1.5',
              highlightLeader && isLeader
                ? 'bg-panel-in border-2 border-orange'
                : 'border border-line',
            )}
          >
            <span className={cn('font-display w-5 text-sm', isLeader ? 'text-orange' : 'text-dim')}>
              {entry.rank}
            </span>
            <PlayerAvatar name={entry.name} src={entry.avatar} size={26} />
            <PlayerLink name={entry.name} className="text-strong font-semibold" />
            <span className="text-dim ml-1 font-mono text-xs">
              {tc('units.games', { count: entry.games })}
            </span>
            <span
              className={cn('ml-auto font-mono text-sm', isLeader ? 'text-gold' : 'text-green')}
            >
              {formatSignedPoints(entry.points)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
